import React, { useEffect, useState, PropsWithChildren } from 'react';
import Box from '@mui/material/Box';
import { gql } from '@yi/core';
import { i18n_preview } from '@yi/i18n/phrases/i18n_preview';

import { CountdownClock, CourseInfoBanner, useLocale } from '~/components';
import { darkGrey } from '~/styles';
import { AnyTypename, isNotNull } from '~/types';

import { CourseButtons } from './CourseButtons';
import { BannerWrapper, BlackOverlay, PlayButton } from './elements';
import { PriceInfo } from './PriceInfo';

type SalesPage = gql.typesSsr.Query_Course_yi_Course_sales_page_select;

interface Props {
  salesPage: AnyTypename<SalesPage>;
  priceInfo: PriceInfo;
  showVideo(): void;
  permissions: gql.typesSsr.Query_Course_yi_Course['permissions'] | null;
  urlTitle: string;
  courseType: 'bundle' | 'ecourse';
}

export const CourseBanner: React.FC<PropsWithChildren<Props>> = ({
  salesPage,
  priceInfo: { originalPrice, discount },
  showVideo,
  permissions,
  urlTitle,
  courseType,
}) => {
  const { locale } = useLocale();
  const [listPrice, setListPrice] = useState(originalPrice || 0);
  const {
    benefits,
    duration,
    lead_image,
    buttons,
    caption_body,
    caption_title,
    countdown,
    countdown_text,
    jwplayer_id,
  } = salesPage;

  useEffect(() => {
    if (buttons?.[0]?.price) setListPrice(buttons[0].price || 0);
  }, [buttons]);

  const amountOff = discount && 'amountOff' in discount.value && discount.value.amountOff;
  const percentOff = discount && 'percentOff' in discount.value && discount.value.percentOff;
  return (
    <Box bgcolor={darkGrey} width="100%" height="auto">
      <BannerWrapper imgUrl={lead_image?.processed_url}>
        <BlackOverlay id="lead-bg">
          <Box mt={10} textAlign="center" mb={0}>
            <Box
              component="h1"
              fontSize={{ xs: '35px', lg: '60px' }}
              style={{
                color: '#fff',
                fontWeight: 400,
                letterSpacing: '0.3px',
                padding: '0 24px',
              }}
            >
              {caption_title}
            </Box>
            <Box display="block" py={3}>
              {caption_body && (
                <Box
                  component="p"
                  color="white"
                  fontWeight="normal"
                  px={{ xs: '22px', lg: 0 }}
                  fontSize={{ xs: 18, lg: 20 }}
                  dangerouslySetInnerHTML={{ __html: caption_body }}
                />
              )}
              {jwplayer_id && (
                <Box alignItems="center" flexDirection="column">
                  <PlayButton onClick={showVideo} />
                  <Box
                    color="white"
                    fontSize={14}
                    my={2}
                    fontWeight={500}
                    letterSpacing={0.4}
                    style={{ textTransform: 'uppercase' }}
                  >
                    {i18n_preview(locale)}
                  </Box>
                </Box>
              )}
              <Box width="100%" textAlign="center">
                <CourseButtons
                  buttons={buttons}
                  listPrice={listPrice}
                  setListPrice={setListPrice}
                  amountOff={amountOff || null}
                  percentOff={percentOff || null}
                  discount={discount}
                  permissions={permissions}
                  urlTitle={urlTitle}
                  courseType={courseType}
                />
                {countdown && new Date(countdown.rss) > new Date() && (
                  <CountdownClock
                    light
                    header={countdown_text || undefined}
                    courseLaunchDate={new Date(countdown.rss)}
                  />
                )}
              </Box>
            </Box>
          </Box>
          <CourseInfoBanner
            duration={duration}
            benefits={benefits ? benefits.filter(isNotNull) : []}
          />
        </BlackOverlay>
      </BannerWrapper>
    </Box>
  );
};
